import logger from '../utils/logger';
import React, { useEffect, useContext, useState } from "react";
import "../style/HomePage.css";
import { Link, useNavigate } from "react-router-dom";
import { GlobalStateContext } from "../utils/GlobalStateContext";
import { getUserId } from "../utils/utils";
import UserDetails from "./customComponents/tracker";
import TopBar from "./customComponents/TopBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Search, ClipboardList, UserCheck, Calendar, User, FileText, DollarSign, FlaskConical, Aperture, FileCheck } from 'lucide-react';

export default function HomePage() {

    const api = {
        searchPatients: async (query, user, type, filter) => {
            try {
                const response = await fetch(`${BASE_URL}/ris/search?pat=${encodeURIComponent(query)}&user=${user}&type=${type}&filter=${filter}`);
                const data = await response.json();
                if (response.status == 200) {
                    return data;
                }
                else {
                    setCurrentPatient([]);
                    return [];
                }
            }
            catch (error) {
                logger.error("Error fetching patients:", error);
            }
        },
    }

    const [message, setMessage] = useState("");
    const { currentUser, setCurrentPatient, currentPatient } = useContext(GlobalStateContext);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchFilter, setSearchFilter] = useState('name');
    const [patients, setPatients] = useState([]);
    const [patientSelected, setPatientSelected] = useState(false);
    const user = getUserId(currentUser);
    const BASE_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();

    const workflowItems = [
        { label: 'Order Worklist', path: '/orderWorklist', Icon: ClipboardList, angle: 0 },
        { label: 'Scheduling', path: '/scheduling', Icon: Calendar, angle: 40 },
        { label: 'Check-In', path: '/checkIn', Icon: UserCheck, angle: 80 },
        { label: 'About Patient', path: '/patientAbout', Icon: User, angle: 120, requiresPatient: true },
        { label: 'Orders', path: '/orders', Icon: FileText, angle: 160, requiresPatient: true },
        { label: 'Payments', path: '/hgiPayments', Icon: DollarSign, angle: 200 },
        { label: 'Labs', path: '/labs', Icon: FlaskConical, angle: 240 },
        { label: 'Radiology', path: '/radiology', Icon: Aperture, angle: 280 },
        { label: 'Results', path: '/results', Icon: FileCheck, angle: 320, requiresPatient: true },
    ];

    const handlePatientRequired = (path) => {
        if (!currentPatient) {
            toast.error('No patient selected. Please search for and select a patient first.');
            return;
        }
        navigate(path);
    };

    const searchFilterOptions = [
        { value: 'name', label: 'Name' },
        { value: 'dob', label: 'Date of Birth' },
        { value: 'mrn', label: 'MRN' },
        { value: 'id', label: 'ID' },
    ];

    const searchPlaceholders = {
        name: 'Search by patient name...',
        dob: 'Search by date of birth (YYYY-MM-DD)...',
        mrn: 'Search by MRN...',
        id: 'Search by RIS patients ID...',
    };

    const handleSearch = async (query) => {
        setSearchQuery(query);
        const results = await api.searchPatients(query, user, currentUser.type, searchFilter);
        setPatients(results);
    };

    useEffect(() => {
        logger.log(currentUser)
    }, []);

    return (
        <div className="home-page">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            <TopBar />

            {/* Main Content */}
            <div className="home-content">
                <div className="home-welcome">
                    <h1>Radiology Information System</h1>
                    <p>Welcome to the Home Page</p>
                </div>

                {/* Search */}
                <div className="home-search-container">
                    <div className="home-search-filters">
                        {searchFilterOptions.map((opt) => (
                            <button
                                key={opt.value}
                                className={`home-search-filter-btn${searchFilter === opt.value ? ' active' : ''}`}
                                onClick={() => { setSearchFilter(opt.value); setSearchQuery(''); setPatients([]); setPatientSelected(false); }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <div className="home-search-bar">
                        <Search className="home-search-icon" size={18} />
                        <input
                            type="text"
                            placeholder={searchPlaceholders[searchFilter]}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSearch(searchQuery);
                            }}
                            className="home-search-input"
                        />
                        <button className="home-search-btn" onClick={() => { handleSearch(searchQuery); setPatientSelected(false); }}>
                            Search
                        </button>
                    </div>
                    {!patientSelected && patients?.length > 0 && (
                        <ul className="home-patient-list">
                            {patients.map((patient) => (
                                <li
                                    key={patient.id}
                                    onClick={() => { setCurrentPatient(patient); setPatientSelected(true); }}
                                    className="home-patient-item"
                                >
                                    {patient.first_name} {patient.last_name} | {patient.dob} | {patient.street_number} {patient.street_name}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Circular Gear Workflow */}
                <div className="workflow-section">
                    <div className="workflow-circle">
                        <div className="workflow-ring"></div>

                        <div className="gear-center">
                            <UserDetails />
                        </div>
                        {workflowItems.map((item) => {
                            const { label, path, Icon, angle, requiresPatient } = item;
                            const rad = (angle - 90) * (Math.PI / 180);
                            const radius = 280;
                            const nodeSize = 110;
                            const x = Math.cos(rad) * radius - nodeSize / 2;
                            const y = Math.sin(rad) * radius - nodeSize / 2;

                            return (
                                <div
                                    key={label}
                                    className="workflow-node"
                                    style={{ transform: `translate(${x}px, ${y}px)` }}
                                >
                                    {requiresPatient ? (
                                        <button
                                            className="workflow-node-btn"
                                            onClick={() => handlePatientRequired(path)}
                                        >
                                            <Icon className="workflow-node-icon" size={28} />
                                            <span className="workflow-node-label">{label}</span>
                                        </button>
                                    ) : (
                                        <Link to={path} className="workflow-node-btn">
                                            <Icon className="workflow-node-icon" size={28} />
                                            <span className="workflow-node-label">{label}</span>
                                        </Link>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
